import styled from "styled-components";
import ChatContainer from "./components/InterviewModeGate";
import FormContainer from "./components/FormContainer";
import FinishContainer from "./components/FinishContainer";
import RecruitmentFinishContainer from "./components/RecruitmentFinishContainer";
import Home from "./components/Home";
import IsFinishedContainer from "./components/IsFinishedContainer";
import AccessErrorContainer from "./components/AccessErrorContainer";
import StatusContainer from "./components/StatusContainer";
import { useState, useEffect } from 'react';
import { getFirestore, setDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import CryptoJS from "crypto-js";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import WWCalc from "./components/WWCalc";
import EIADenuncia from "./components/Denuncia";
import DenunciaAvatarContainer from "./components/DenunciaAvatarContainer";
import Publicidade from "./components/Publicidade";
import Aprofundamento from "./components/Aprofundamento";
import { normalizeInterviewTypeKey, isRecruitmentType } from "./utils/interviewType";
import { fetchInterviewByAccessToken } from "./services/interviewService";
import { caminhoDaEntrevista, caminhoDoCaso } from './utils/caminhosConta';

const getSaoPauloDate = () => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(new Date()).reduce((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});
  return new Date(`${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}-03:00`);
};


const getCompanyFromPath = () => {
  const pathname = window.location.pathname || '';
  const match = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
  return match ? match[1] : '';
};

const routeInterviewTypeFromPath = () => {
  const pathname = window.location.pathname || '';
  const match = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
  return match ? match[2] : '';
};

export default function App() {
  const [showFormContainer, setShowFormContainer] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showFinishContainer, setShowFinishContainer] = useState(false);
  const [showIsFinished, setShowIsFinished] = useState(false);
  const [showAccessError, setShowAccessError] = useState(false);
  const [user, setUser] = useState({ nome: "", nomesocial:"", cpf: "", empresa: "", setor: "", cargo: "", liderança: "", email: ""})
  const [interviewType, setInterviewType] = useState('');
  const [company, setCompany] = useState(getCompanyFromPath());
  const initialCompanyFromPath = getCompanyFromPath();
  const isRecruitmentFlowCurrent = isRecruitmentType(interviewType || user.entrevista || routeInterviewTypeFromPath());

  const parseDeadline = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value?.toDate) return value.toDate();
    if (typeof value === 'object' && value.seconds) {
      return new Date(value.seconds * 1000);
    }
    return null;
  };

  useEffect(() => {
    const fetchInterviewType = async () => {
      try {
        const pathname = window.location.pathname || '';
        if (!pathname.startsWith('/chat')) {
          return;
        }
        const encryptedData = new URLSearchParams(window.location.search).get("data");
        if (!encryptedData) {
          throw new Error("Parâmetro 'data' não encontrado na URL");
        }

        const localStorageKey = `cliqueRegistrado_${encryptedData}`;
        const alreadyClicked = localStorage.getItem(localStorageKey);

        const decodedData = decodeURIComponent(encryptedData);
        const secretKey = "due-prd";
        const decryptedBase64 = CryptoJS.AES.decrypt(decodedData, secretKey).toString(CryptoJS.enc.Utf8);

        const decryptedString = atob(decryptedBase64);

        const decryptedParams = JSON.parse(decryptedString);

        const { denuncia, entrevistaId, company: companyParam } = decryptedParams;
        const interviewTypeKey = decryptedParams.entrevista || decryptedParams.tipoEntrevista || 'pesquisa-de-compliance';
        const storageInterviewType = normalizeInterviewTypeKey(interviewTypeKey);
        const normalizedCompany = companyParam && companyParam._value ? companyParam._value : companyParam;

        setCompany(normalizedCompany);
        setInterviewType(storageInterviewType);

        const db = getFirestore();
        if (!alreadyClicked) {
          const clickRef = doc(db, caminhoDoCaso({ empresa: normalizedCompany, tipo: storageInterviewType, caso: denuncia }));
          const clickSnap = await getDoc(clickRef);
          const currentCount = clickSnap.exists() ? clickSnap.data().count || 0 : 0;
          await setDoc(clickRef, {
            count: currentCount + 1,
            lastClick: getSaoPauloDate()
          }, { merge: true });

          // Marca que esse `data` já contou
          localStorage.setItem(localStorageKey, "true");
        }
        
        // Busca apenas no caminho novo alinhado com o restante da aplicação
        const entrevistaDocRef = doc(db, caminhoDaEntrevista({ empresa: normalizedCompany, tipo: storageInterviewType, caso: denuncia, entrevista: entrevistaId }));
        const docSnap = await getDoc(entrevistaDocRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setInterviewType(normalizeInterviewTypeKey(data.tipoEntrevista || interviewTypeKey));
          // Preenche user com dados já salvos (para evitar reabrir formulário)
          setUser(prev => ({
            ...prev,
            nome: data.nome || prev.nome,
            nomesocial: data.nomesocial || prev.nomesocial,
            cpf: data.cpf || prev.cpf,
            numeromatricula: data.numeromatricula || prev.numeromatricula,
            empresa: data.empresa || prev.empresa,
            setor: data.setor || prev.setor,
            cargo: data.cargo || prev.cargo,
            liderança: data.liderança || prev.liderança,
            email: data.email || prev.email,
            company: normalizedCompany,
            denuncia,
            entrevistaId
          }));

          const entrevistaRef = doc(db, caminhoDaEntrevista({ empresa: normalizedCompany, tipo: storageInterviewType, caso: denuncia, entrevista: entrevistaId }));
          await updateDoc(entrevistaRef, {
            firstAccessAt: getSaoPauloDate()
          });

          // Se já finalizou, bloqueia o fluxo
          if (data.finalEntrevista) {
            let prazo = parseDeadline(
              data.prazoLimite ||
              data.prazoFinal ||
              data.prazoFim ||
              data.deadlineAt ||
              data.deadline
            );
            try {
              const denunciaRef = doc(db, caminhoDoCaso({ empresa: normalizedCompany, tipo: storageInterviewType, caso: denuncia }));
              const denunciaSnap = await getDoc(denunciaRef);
              if (denunciaSnap.exists()) {
                const denunciaData = denunciaSnap.data() || {};
                prazo = prazo || parseDeadline(denunciaData.data);
              }
            } catch (err) {
              console.warn('Não foi possível buscar data limite na denúncia:', err);
            }
            const dentroDoPrazo = !prazo || prazo.getTime() >= Date.now();
            const pesquisaFinalizada = Boolean(data.pesquisaFinalizada || data.finalizada || data.encerrada);
            const canAttachEvidence = !pesquisaFinalizada && dentroDoPrazo;
            if (canAttachEvidence) {
              setShowFinishContainer(true);
              setShowIsFinished(false);
              setShowFormContainer(false);
              setShowChat(false);
              return;
            }
            setShowIsFinished(true);
            setShowFormContainer(false);
            setShowChat(false);
            return;
          }

          // Se o formulário já foi enviado ou há progresso salvo, pula direto para o chat
          const hasProgress = (Array.isArray(data.responses) && data.responses.length) ||
            (Array.isArray(data.messages) && data.messages.length);
          if (data.inicioEntrevista || hasProgress) {
            setShowFormContainer(false);
            setShowChat(true);
          } else if (isRecruitmentType(data.tipoEntrevista || interviewTypeKey)) {
            // Em recrutamento, entra direto no formulário inicial com dados pré-carregados.
            setShowFormContainer(true);
            setShowChat(false);
          }
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching document:", error);
      }
    };

    fetchInterviewType();
  }, []);

  useEffect(() => {
    const fetchInterviewByRoute = async () => {
      const pathname = window.location.pathname || '';
      const match = pathname.match(/^\/([^/]+)\/([^/]+)\/([^/]+)\/entrevista\/([^/]+)$/);
      if (!match) return;

      const [, companyFromPath, interviewTypeKey, denunciaFromPath, entrevistaId] = match;
      const token = (new URLSearchParams(window.location.search).get('t') || '').toString().trim();

      try {
        const normalizedCompany = (companyFromPath || '').toLowerCase();
        const storageInterviewType = normalizeInterviewTypeKey(interviewTypeKey);
        const result = await fetchInterviewByAccessToken({
          company: normalizedCompany,
          interviewTypeKey: storageInterviewType,
          denuncia: denunciaFromPath,
          entrevistaId,
          token
        });
        const data = result?.interview || null;
        if (!data) {
          console.error('Entrevista não encontrada para o id informado.');
          setShowIsFinished(true);
          return;
        }
        const nomeInvestigadoDoc = result?.denuncia?.nomeInvestigado || null;
        const deadlineFromDenuncia = parseDeadline(result?.denuncia?.deadline);

        setCompany(normalizedCompany);
        setInterviewType(normalizeInterviewTypeKey(data.tipoEntrevista || interviewTypeKey));

        setUser(prev => ({
          ...prev,
          nome: data.nome || prev.nome,
          nomesocial: data.nomesocial || prev.nomesocial,
          cpf: data.cpf || prev.cpf,
          numeromatricula: data.numeromatricula || prev.numeromatricula,
          ...(data.matricula ? { numeromatricula: data.matricula } : {}),
          empresa: data.empresa || prev.empresa,
          setor: data.setor || prev.setor,
          cargo: data.cargo || prev.cargo,
          liderança: data.liderança || nomeInvestigadoDoc || prev.liderança,
          email: data.email || prev.email,
          company: normalizedCompany,
          denuncia: denunciaFromPath,
          entrevistaId,
          entrevista: normalizeInterviewTypeKey(data.tipoEntrevista || interviewTypeKey)
        }));

        if (data.finalEntrevista) {
          const isRecruitmentFlow = isRecruitmentType(data.tipoEntrevista || interviewTypeKey);
          if (isRecruitmentFlow) {
            setShowIsFinished(true);
            setShowFormContainer(false);
            setShowChat(false);
            setShowFinishContainer(false);
            return;
          }

          let prazo = parseDeadline(
            data.prazoLimite ||
            data.prazoFinal ||
            data.prazoFim ||
            data.deadlineAt ||
            data.deadline
          ) || deadlineFromDenuncia;
          const dentroDoPrazo = !prazo || prazo.getTime() >= Date.now();
          const pesquisaFinalizada = Boolean(data.pesquisaFinalizada || data.finalizada || data.encerrada);
          const canAttachEvidence = !pesquisaFinalizada && dentroDoPrazo;
          if (canAttachEvidence) {
            setShowFinishContainer(true);
            setShowIsFinished(false);
            setShowFormContainer(false);
            setShowChat(false);
            return;
          }
          setShowIsFinished(true);
          setShowFormContainer(false);
          setShowChat(false);
          return;
        }

        const hasProgress = (Array.isArray(data.responses) && data.responses.length) ||
          (Array.isArray(data.messages) && data.messages.length);
        if (data.inicioEntrevista || hasProgress) {
          setShowFormContainer(false);
          setShowChat(true);
        } else if (isRecruitmentType(data.tipoEntrevista || interviewTypeKey)) {
          setShowFormContainer(true);
          setShowChat(false);
        } else {
          setShowFormContainer(false);
          setShowChat(false);
        }
      } catch (error) {
        // RS3: token invalido/adulterado cai aqui (getInterviewByAccessToken rejeita no
        // backend). Antes, isso so logava no console e a tela seguia mostrando intro/form/tour
        // normalmente, sem avisar o candidato de que o link não é valido e nada sera salvo.
        console.error('Erro ao buscar entrevista pela rota:', error);
        if (token) {
          setShowAccessError(true);
        }
      }
    };

    fetchInterviewByRoute();
  }, []);

  const playBackgroundVideo = () => {
    const videoElement = document.querySelector('video');
    if (videoElement) {
      videoElement.play().catch(error => {
        console.error("Error trying to autoplay video with sound:", error);
      });
    }
  };

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/:company/:interviewType/:denuncia/entrevista/:entrevistaId" element={
          <PageContainer>
            {showAccessError ? (
              <AccessErrorContainer />
            ) : showIsFinished ? (
              <IsFinishedContainer />
            ) : (
              <>
                {!showFormContainer && !showFinishContainer && !showChat && (
                  <Home setShowFormContainer={setShowFormContainer} company={company} initialCompany={initialCompanyFromPath}/>
                )}
                {!showChat && showFormContainer && !showFinishContainer &&
                <FormContainer 
                setShowChat={setShowChat} 
                user={user} 
                setUser={setUser} 
                playBackgroundVideo={playBackgroundVideo}
                />}
                {showChat && !showFinishContainer && (
                  <ChatContainer user={user} setShowFinishContainer={setShowFinishContainer} interviewType={interviewType} />
                )} 
                {showFinishContainer && !isRecruitmentFlowCurrent && <FinishContainer company={company}/>}
                {showFinishContainer && isRecruitmentFlowCurrent && <RecruitmentFinishContainer />}
              </>
            )}
          </PageContainer>
        } />
        <Route path="/chat" element={
          <PageContainer>
            {showAccessError ? (
              <AccessErrorContainer />
            ) : showIsFinished ? (
              <IsFinishedContainer />
            ) : (
              <>
                {!showFormContainer && !showFinishContainer && !showChat && (
                  <Home setShowFormContainer={setShowFormContainer} company={company} initialCompany={initialCompanyFromPath}/>
                )}
                {!showChat && showFormContainer && !showFinishContainer &&
                <FormContainer 
                setShowChat={setShowChat} 
                user={user} 
                setUser={setUser} 
                playBackgroundVideo={playBackgroundVideo}
                />}
                {showChat && !showFinishContainer && (
                  <ChatContainer user={user} setShowFinishContainer={setShowFinishContainer} interviewType={interviewType} />
                )} 
                {showFinishContainer && !isRecruitmentFlowCurrent && <FinishContainer company={company}/>}
                {showFinishContainer && isRecruitmentFlowCurrent && <RecruitmentFinishContainer />}
              </>
            )}
          </PageContainer>
        } />
        <Route path="/:company/:interviewType/:denuncia/entrevista/:entrevistaId/status" element={<StatusContainer />} />
        <Route path="/aprofundamento/:denuncia" element={<Aprofundamento />} />
        <Route path="/denuncia/:company" element={<EIADenuncia />} />
        <Route path="/:company/denuncia-com-avatar" element={<DenunciaAvatarContainer />} />
        <Route path="/wwcalc" element={<WWCalc />} />
        <Route path="/publicidade" element={<Publicidade />} />
      </Routes>
    </Router>
  );
}

const PageContainer = styled.div`
  width: 100% !important;
  height: 100vh;
  background-color: #F8F9FB;
  display: flex;
  align-items: center;
  justify-content: center;
  @media only screen and (max-width: 480px) {
    height: auto;
  }
`



